'use client';

import Link from 'next/link';
import Image from '@/components/Image';
import { useState, useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import type { TextbookBook } from '@/app/textbooks/page';
import PublicSiteHeader from '@/components/public-site/PublicSiteHeader';
import PublicSiteFooter from '@/components/public-site/PublicSiteFooter';

type Props = {
  books: TextbookBook[];
};

function gradeOrder(grade: string): number {
  if (!grade) return 99;
  const lower = grade.toLowerCase();
  if (lower.includes('k')) return 0;
  const m = grade.match(/(\d{1,2})/);
  return m ? Number(m[1]) : 99;
}

function FilterPills({
  label,
  options,
  active,
  onToggle,
}: {
  label: string;
  options: string[];
  active: string | null;
  onToggle: (v: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="display-text-semi w-16 shrink-0 text-xs uppercase tracking-[0.16em] text-[#8A96A8]">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt}
            onClick={() => onToggle(opt)}
            className={`display-text-semi rounded-full px-3 py-1 text-xs transition-all ${
              active === opt
                ? 'bg-[#0D1929] text-white'
                : 'border border-[#D8E3EF] bg-white text-[#4A5A72] hover:border-[#42C52D] hover:text-[#42C52D]'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PublicTextbooksGrid({ books }: Props) {
  const [activeGrade, setActiveGrade] = useState<string | null>(null);
  const [activeSubject, setActiveSubject] = useState<string | null>(null);
  const [activeSeries, setActiveSeries] = useState<string | null>(null);

  // Series options always show full list
  const seriesList = useMemo(
    () => [...new Set(books.map((b) => b.series).filter(Boolean))].sort(),
    [books],
  );

  // Books scoped to active series (used to derive cascaded grade/subject options)
  const seriesScoped = useMemo(
    () => (activeSeries ? books.filter((b) => b.series === activeSeries) : books),
    [books, activeSeries],
  );

  // Grade/subject options cascade from series selection
  const grades = useMemo(
    () =>
      [...new Set(seriesScoped.map((b) => b.grade).filter(Boolean))].sort(
        (a, b) => gradeOrder(a) - gradeOrder(b),
      ),
    [seriesScoped],
  );

  const subjects = useMemo(
    () => [...new Set(seriesScoped.map((b) => b.subject).filter(Boolean))].sort(),
    [seriesScoped],
  );

  const filtered = useMemo(
    () =>
      books.filter((b) => {
        if (activeGrade && b.grade !== activeGrade) return false;
        if (activeSubject && b.subject !== activeSubject) return false;
        if (activeSeries && b.series !== activeSeries) return false;
        return true;
      }),
    [books, activeGrade, activeSubject, activeSeries],
  );

  const hasActiveFilter = activeGrade || activeSubject || activeSeries;

  const clearAll = () => {
    setActiveGrade(null);
    setActiveSubject(null);
    setActiveSeries(null);
  };

  return (
    <>
    <PublicSiteHeader />
    <main
      className="min-h-screen"
      style={{
        background: '#F4F7FB',
        backgroundImage: 'radial-gradient(circle, #C8D6E8 1px, transparent 1px)',
        backgroundSize: '26px 26px',
      }}
    >
      <div className="mx-auto max-w-[1400px] px-4 pt-8 pb-10 md:px-10 md:pt-10">
        {/* breadcrumb */}
        <div className="mb-8">
          <Link
            href="/"
            className="display-text-semi inline-flex items-center gap-1.5 rounded-full border border-[#D8E3EF] bg-white/80 px-4 py-2 text-sm text-[#8A96A8] shadow-sm backdrop-blur-sm transition hover:border-[#C5EDB8] hover:text-[#42C52D]"
          >
            Pengi
            <span className="mx-0.5 text-[#C8D2DE]">/</span>
            <span className="text-[#1F2A44]">Textbook Plans</span>
          </Link>
        </div>

        {/* hero */}
        <div className="flex flex-col gap-6 px-1 md:px-2 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-[1080px]">
            <h1 className="emphasize-text text-[52px] leading-[1.04] tracking-[-0.035em] text-[#0D1929] md:text-[80px]">
              Pengi Textbook Plans
            </h1>
            <p className="display-text mt-5 max-w-[870px] text-base leading-[1.8] text-[#4A5A72] md:text-lg">
              Browse the full Pengi book collection. Filter by grade, subject, or series to find the right book, then jump straight into lessons and practice.
            </p>
            <p className="display-text mt-4 max-w-[860px] text-sm leading-[1.8] text-[#6B7A90] md:text-base">
              These textbook plans are designed for families who want learning support connected to
              what students actually study in school. Instead of searching random worksheets or
              generic AI answers, you can open the right book, find the right chapter, and move
              straight into guided practice.
            </p>
          </div>

        </div>

        {/* filters */}
        <div className="mx-1 mt-8 rounded-[24px] border border-[#E2EAF4] bg-white/80 px-5 py-5 shadow-sm backdrop-blur-sm md:mx-2">
          <div className="flex flex-col gap-4">
            <FilterPills
              label="Series"
              options={seriesList}
              active={activeSeries}
              onToggle={(v) => {
                setActiveSeries(activeSeries === v ? null : v);
                setActiveGrade(null);
                setActiveSubject(null);
              }}
            />
            <FilterPills
              label="Grade"
              options={grades}
              active={activeGrade}
              onToggle={(v) => setActiveGrade(activeGrade === v ? null : v)}
            />
            <FilterPills
              label="Subject"
              options={subjects}
              active={activeSubject}
              onToggle={(v) => setActiveSubject(activeSubject === v ? null : v)}
            />
            {hasActiveFilter && (
              <button
                onClick={clearAll}
                className="display-text-semi self-start text-xs text-[#8A96A8] underline transition hover:text-[#1F2A44]"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* book grid */}
      <div className="mx-auto max-w-[1400px] px-4 pb-16 md:px-10">
        <div className="mb-4 px-1 md:px-2">
          <span className="display-text text-sm text-[#8A96A8]">
            {filtered.length} {filtered.length === 1 ? 'book' : 'books'}
            {hasActiveFilter ? ' matching filters' : ''}
          </span>
        </div>

        {filtered.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 md:gap-5">
            {filtered.map((book) => (
              <Link key={book.id} href={book.href} className="group block">
                <div
                  className="overflow-hidden rounded-[18px] bg-white transition-all duration-200 ease-out group-hover:-translate-y-1"
                  style={{
                    boxShadow:
                      '0 4px 16px rgba(31,42,68,0.08), 0 0 0 1px rgba(31,42,68,0.04)',
                  }}
                >
                  <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#EEF2F8]">
                    <Image
                      src={book.coverImage}
                      alt={book.title}
                      width={300}
                      height={400}
                      className="h-full w-full object-cover"
                      optimizedWidth={400}
                    />
                    <div className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-gradient-to-r from-black/[0.1] to-transparent" />
                    {book.grade && (
                      <div className="display-text-semi absolute right-2 top-2 rounded-full bg-white/90 px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[#4A5A72] shadow-sm">
                        {book.grade}
                      </div>
                    )}
                  </div>

                  <div className="px-3 pb-3 pt-3">
                    <div className="emphasize-text line-clamp-2 text-[13px] leading-snug text-[#0D1929]">
                      {book.title}
                    </div>
                    {book.series && (
                      <div className="display-text mt-1 line-clamp-1 text-[11px] text-[#8A96A8]">
                        {book.series}
                      </div>
                    )}
                    <div className="mt-2 flex items-center justify-end">
                      <span className="display-text-semi flex items-center gap-0.5 text-[11px] text-[#42C52D]">
                        Open
                        <ArrowRight className="h-2.5 w-2.5 transition-transform duration-150 group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="py-20 text-center">
            <p className="display-text text-base text-[#8A96A8]">No books match the selected filters.</p>
            <button
              onClick={clearAll}
              className="display-text-semi mt-4 text-sm text-[#42C52D] underline"
            >
              Clear all filters
            </button>
          </div>
        )}

        <section className="mt-14 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-[24px] border border-[#E2EAF4] bg-white p-6 shadow-sm md:p-7">
            <h2 className="emphasize-text text-[28px] leading-[1.15] text-[#0D1929]">
              How to use textbook plans
            </h2>
            <div className="mt-5 space-y-4 text-sm leading-[1.8] text-[#566070] md:text-[15px]">
              <p>
                Start by filtering to your child&apos;s grade, subject, or textbook series. Once
                you open a book, you can jump directly into the matching lessons and practice
                instead of trying to rebuild the curriculum yourself.
              </p>
              <p>
                This works especially well for families who want textbook-aligned support, students
                who need help staying on pace with school, and parents looking for a more structured
                alternative to generic AI homework tools.
              </p>
            </div>
          </div>

          <div className="rounded-[24px] border border-[#E2EAF4] bg-white p-6 shadow-sm md:p-7">
            <h2 className="emphasize-text text-[28px] leading-[1.15] text-[#0D1929]">
              Related Pengi guides
            </h2>
            <div className="mt-5 space-y-4">
              <Link
                href="/textbook-aligned-ai-tutor"
                className="block rounded-[18px] border border-[#E8EFF8] bg-[#FAFCFE] p-4 transition hover:border-[#42C52D]/40"
              >
                <p className="display-text-semi text-sm text-[#0D1929]">Textbook-aligned AI tutor</p>
                <p className="display-text mt-1 text-sm leading-[1.7] text-[#6B7A90]">
                  Learn how Pengi connects AI support directly to real school materials.
                </p>
              </Link>
              <Link
                href="/ai-tutor-for-kids"
                className="block rounded-[18px] border border-[#E8EFF8] bg-[#FAFCFE] p-4 transition hover:border-[#42C52D]/40"
              >
                <p className="display-text-semi text-sm text-[#0D1929]">AI tutor for kids</p>
                <p className="display-text mt-1 text-sm leading-[1.7] text-[#6B7A90]">
                  See the main family-facing tutoring page for grades 3 through 11.
                </p>
              </Link>
              <Link
                href="/homework-help-without-giving-answers"
                className="block rounded-[18px] border border-[#E8EFF8] bg-[#FAFCFE] p-4 transition hover:border-[#42C52D]/40"
              >
                <p className="display-text-semi text-sm text-[#0D1929]">Homework help without giving answers</p>
                <p className="display-text mt-1 text-sm leading-[1.7] text-[#6B7A90]">
                  Explore Pengi&apos;s guided approach for daily homework support.
                </p>
              </Link>
            </div>
          </div>
        </section>

        <section className="mt-10 rounded-[24px] border border-[#E2EAF4] bg-white p-6 shadow-sm md:p-7">
          <h2 className="emphasize-text text-[28px] leading-[1.15] text-[#0D1929]">
            Textbook plans FAQ
          </h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2">
            {[
              {
                q: 'What is a textbook plan?',
                a: 'A textbook plan organizes lessons and practice around a specific school book so students can get help in the same sequence they see in class.',
              },
              {
                q: 'Who should use the textbooks page?',
                a: 'Parents, students, and educators who want to find support by grade, subject, or series instead of searching one concept at a time.',
              },
              {
                q: 'How is this different from generic homework help?',
                a: 'Generic tools answer isolated questions. Textbook plans help students stay anchored to the actual material and structure they are expected to learn.',
              },
              {
                q: 'Can I use textbook plans with AI tutoring?',
                a: 'Yes. The textbook collection works well with Pengi&apos;s guided tutoring pages, especially for textbook-aligned learning and homework support.',
              },
            ].map((item) => (
              <div key={item.q} className="rounded-[18px] border border-[#E8EFF8] bg-[#FAFCFE] p-5">
                <h3 className="display-text-semi text-sm text-[#0D1929]">{item.q}</h3>
                <p className="display-text mt-2 text-sm leading-[1.7] text-[#566070]">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </main>
    <PublicSiteFooter />
    </>
  );
}
